import { Redirect, Route } from "found";


import CreatePolicyPage from "./create_policy/CreatePolicyPage";
import CreatePolicyQuery from "./create_policy/CreatePolicyQuery";
import DeletePolicy from "./delete_policy/DeletePolicyPage";
import EditPolicyPage from "./edit_policy/EditPolicyPage";
import EditPolicyQuery from "./edit_policy/EditPolicyQuery";
import OverridePolicyPage from "./override_policy/OverridePolicyPage";
import OverridePolicyQuery from "./override_policy/OverridePolicyQuery";
import PolicyRevisions from "./policy_revisions/PolicyRevisions";
import PolicyRevisionsQuery from "./policy_revisions/PolicyRevisionsQuery";
import RetirePolicyPage from "./retire_policy/RetirePolicyPage";
import RetirePolicyQuery from "./retire_policy/RetirePolicyQuery";
import UnsubscribedFromCommentsPage from "./view_policy/UnsubscribedFromCommentsPage";
import UnsubscribedFromCommentsQuery from "./view_policy/UnsubscribedFromCommentsQuery";
import ViewPolicy from "./view_policy/ViewPolicy";
import ViewPolicyQuery from "./view_policy/ViewPolicyQuery";

import { checkForRedirect } from "pstat-anywhere/utils/urls";
import { ErrorCheckWrapper } from "pstat-anywhere/utils/routes";
import {
  TriggerEventOnMount,
  triggerPolicyViewEvent
} from "pstat-anywhere/utils/googleTagManager";

const routes = (
  <Route>
    <Route
      path="policy/create"
      Component={CreatePolicyPage}
      query={CreatePolicyQuery}
      data={{
        routeName: "document_create"
      }}
      prepareVariables={(params, { location }) => ({
        ...params,
        templatePk: location.query.template
          ? `${location.query.template}`
          : null,
        documentSavePk: location.query.documentSavePk
          ? `${location.query.documentSavePk}`
          : null
      })}
      render={({ props }) => {
        checkForRedirect(props, "documentCreate");
        return (
          <ErrorCheckWrapper queryName="documentCreate" {...props}>
            <CreatePolicyPage {...props} />
          </ErrorCheckWrapper>
        );
      }}
    />
    {/*
      Create a different path for creating a new document from the nav so that
      if already on the create page it will reload the page.
      Previously navigating to the create page wouldn't reload the page if already there.
    */}
    <Redirect
      from="policy/create/new"
      to={match => {
        const search = match.location.search;
        let path = "/policy/create";
        if (search) {
          path += search;
        }
        return path;
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/create"
      Component={CreatePolicyPage}
      query={CreatePolicyQuery}
      data={{
        routeName: "document_create"
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentCreate");
        return (
          <ErrorCheckWrapper queryName="documentCreate" {...props}>
            <CreatePolicyPage {...props} />
          </ErrorCheckWrapper>
        );
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/edit"
      Component={EditPolicyPage}
      query={EditPolicyQuery}
      data={{
        routeName: "document_edit"
      }}
      prepareVariables={(params, { location }) => ({
        ...params,
        continueDraft: !!location.query.continue_draft
      })}
      render={({ props }) => {
        checkForRedirect(props, "documentEdit");
        return (
          <ErrorCheckWrapper queryName="documentEdit" {...props}>
            <EditPolicyPage {...props} />
          </ErrorCheckWrapper>
        );
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/admin_override"
      Component={OverridePolicyPage}
      query={OverridePolicyQuery}
      data={{
        routeName: "document_override"
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentOverride");
        return (
          <ErrorCheckWrapper queryName="documentOverride" {...props}>
            <OverridePolicyPage {...props} />
          </ErrorCheckWrapper>
        );
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/approve"
      Component={ViewPolicy}
      query={ViewPolicyQuery}
      data={{
        routeName: "document_approve"
      }}
      prepareVariables={(params, { location }) => {
        let { showchanges } = location.query;
        showchanges = !!showchanges;
        return {
          ...params,
          showChanges: showchanges,
          latest: false,
          ignoreLatestRedirect: true
        };
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentView");
        if (props && props.documentView) {
          return <ViewPolicy {...props} />;
        }
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/latest"
      Component={ViewPolicy}
      query={ViewPolicyQuery}
      data={{
        routeName: "document_view"
      }}
      prepareVariables={(params, { location }) => {
        let { showchanges } = location.query;
        showchanges = !!showchanges;
        return {
          ...params,
          showChanges: showchanges,
          latest: true
        };
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentView", true);
        if (props && props.documentView) {
          return (
            <ErrorCheckWrapper queryName="documentView" {...props}>
              <TriggerEventOnMount
                triggerEvent={() => {
                  const document = props.documentView.document;
                  if (document) {
                    triggerPolicyViewEvent(
                      document.name,
                      document.category.name,
                      document.pk
                    );
                  }
                }}
              >
                <ViewPolicy {...props} />
              </TriggerEventOnMount>
            </ErrorCheckWrapper>
          );
        }
      }}
    />
    <Route
      path="policy/:documentPk(\d+)"
      Component={ViewPolicy}
      query={ViewPolicyQuery}
      data={{
        routeName: "document_view"
      }}
      prepareVariables={(params, { location }) => {
        let { showchanges } = location.query;
        showchanges = !!showchanges;
        return {
          ...params,
          showChanges: showchanges,
          latest: false
        };
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentView", true);
        if (props && props.documentView) {
          return (
            <ErrorCheckWrapper queryName="documentView" {...props}>
              <TriggerEventOnMount
                triggerEvent={() => {
                  const document = props.documentView.document;
                  if (document) {
                    triggerPolicyViewEvent(
                      document.name,
                      document.category.name,
                      document.pk
                    );
                  }
                }}
              >
                <ViewPolicy {...props} />
              </TriggerEventOnMount>
            </ErrorCheckWrapper>
          );
        }
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/delete"
      Component={DeletePolicy}
      query={ViewPolicyQuery}
      data={{
        routeName: "document_delete"
      }}
      prepareVariables={params => {
        return {
          ...params,
          latest: false,
          ignoreLatestRedirect: true
        };
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentView");
        if (props && props.documentView) {
          return <DeletePolicy {...props} />;
        }
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/revisions"
      Component={PolicyRevisions}
      query={PolicyRevisionsQuery}
      data={{
        routeName: "document_revisions"
      }}
      render={({ props }) => {
        checkForRedirect(props, "documentRevisions");
        return (
          <ErrorCheckWrapper queryName="documentRevisions" {...props}>
            <PolicyRevisions {...props} />
          </ErrorCheckWrapper>
        );
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/retire"
      query={RetirePolicyQuery}
      data={{
        routeName: "document_retire"
      }}
      render={({ props }) => {
        if (props && props.documentRetire) {
          return (
            <ErrorCheckWrapper queryName="documentRetire" {...props}>
              <RetirePolicyPage {...props} />
            </ErrorCheckWrapper>
          );
        }
      }}
    />
    <Route
      path="policy/:documentPk(\d+)/commentsunsubscribed"
      query={UnsubscribedFromCommentsQuery}
      data={{
        routeName: "document_unsubscribed_from_comments"
      }}
      render={({ props }) => {
        if (props && props.unsubscribedDocument) {
          return <UnsubscribedFromCommentsPage {...props} />;
        }
      }}
    />
  </Route>
);

export default routes;
